import React, { useState, useEffect } from 'react'
import { Button, Space } from 'antd'
import MaterialIcon from './MaterialIcon'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import PriceBookSelector from './PriceBookSelector'

interface PriceBookItem {
  id: string
  name: string
  description?: string
  unitPrice: number
  unit?: string
  quantity: number
  subtotal: number
}

interface EmailComposerProps {
  onSend: (body: string) => void
  onCancel: () => void
  isSending: boolean
  initialValue?: string
}

const EmailComposer: React.FC<EmailComposerProps> = ({
  onSend,
  onCancel,
  isSending,
  initialValue = '',
}) => {
  const [body, setBody] = useState(initialValue)
  const [showPriceBook, setShowPriceBook] = useState(false)

  // Update body when initialValue changes
  useEffect(() => {
    setBody(initialValue)
  }, [initialValue])

  const handleSend = () => {
    if (!body.trim()) {
      return
    }
    onSend(body)
  }

  const formatItemsAsQuote = (items: PriceBookItem[]) => {
    const subtotal = items.reduce((sum, item) => sum + item.subtotal, 0)
    const tax = subtotal * 0.08 // 8% tax - should be configurable
    const total = subtotal + tax

    // Create hidden metadata with item IDs for easy extraction later
    const metadata = {
      type: 'price_quote',
      timestamp: new Date().toISOString(),
      items: items.map(item => ({
        id: item.id,
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        subtotal: item.subtotal,
      })),
      subtotal,
      tax,
      total,
    }
    
    console.log('💾 Generated metadata:', JSON.stringify(metadata, null, 2))

    const itemsList = items.map((item, idx) => {
      const description = item.description 
        ? `<div style="margin-left: 20px; color: #666; font-size: 13px; font-style: italic; margin-top: 4px; margin-bottom: 8px;">${item.description}</div>`
        : '<br/>'
      
      return `
        <div style="padding: 12px; background: white; border-radius: 6px; margin-bottom: 10px; border-left: 4px solid #1890ff;">
          <div style="display: flex; justify-content: space-between; align-items: start;">
            <div style="flex: 1;">
              <strong style="font-size: 15px; color: #1890ff;">${idx + 1}. ${item.name}</strong>
              ${description}
            </div>
            <div style="text-align: right; margin-left: 20px;">
              <div style="color: #666; font-size: 13px;">Qty: ${item.quantity} × $${item.unitPrice.toFixed(2)}</div>
              <div style="font-size: 18px; font-weight: bold; color: #1890ff; margin-top: 4px;">$${item.subtotal.toFixed(2)}</div>
            </div>
          </div>
        </div>
      `
    }).join('')

    const quoteHtml = `
      <!-- QUOTE_METADATA:${btoa(JSON.stringify(metadata))} -->
      <div style="margin: 24px 0; padding: 24px; border: 2px solid #1890ff; border-radius: 12px; background: linear-gradient(135deg, #f0f7ff 0%, #e6f4ff 100%); font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        
        <div style="text-align: center; margin-bottom: 20px;">
          <h2 style="margin: 0; color: #1890ff; font-size: 24px; font-weight: 600;">
            📋 Price Quote
          </h2>
          <div style="height: 3px; width: 60px; background: #1890ff; margin: 12px auto 0;"></div>
        </div>

        <div style="background: #f8f9fa; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
          <div style="color: #666; font-size: 14px; font-weight: 500; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Items</div>
          ${itemsList}
        </div>

        <div style="background: white; padding: 20px; border-radius: 8px; border: 1px solid #d9d9d9;">
          <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0;">
            <span style="color: #666; font-size: 15px;">Subtotal:</span>
            <span style="font-size: 16px; font-weight: 500;">$${subtotal.toFixed(2)}</span>
          </div>
          
          <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0;">
            <span style="color: #666; font-size: 15px;">Tax (8%):</span>
            <span style="font-size: 16px; font-weight: 500;">$${tax.toFixed(2)}</span>
          </div>
          
          <div style="display: flex; justify-content: space-between; padding: 16px 0 0 0; margin-top: 8px; border-top: 2px solid #1890ff;">
            <span style="color: #1890ff; font-size: 18px; font-weight: 600;">TOTAL:</span>
            <span style="color: #1890ff; font-size: 24px; font-weight: bold;">$${total.toFixed(2)}</span>
          </div>
        </div>

        <div style="margin-top: 20px; padding: 16px; background: #fffbe6; border-left: 4px solid #faad14; border-radius: 4px;">
          <div style="color: #666; font-size: 13px; line-height: 1.6;">
            <strong style="color: #d48806;">📌 Note:</strong> This quote is valid for <strong>30 days</strong> from the date of this email.
          </div>
        </div>

      </div>
      <p><br/></p>
    `

    return quoteHtml
  }

  const handleAddItems = (items: PriceBookItem[]) => {
    console.log('📦 Adding items to email:', items)
    const quoteHtml = formatItemsAsQuote(items)
    console.log('📝 Generated quote HTML length:', quoteHtml.length)
    console.log('📝 First 500 chars:', quoteHtml.substring(0, 500))
    
    // Use functional update to ensure we get the latest state
    setBody(prevBody => {
      const currentBody = prevBody || ''
      const newBody = currentBody + quoteHtml
      console.log('✅ Setting new body, total length:', newBody.length)
      return newBody
    })
    
    // Verify update after state change
    setTimeout(() => {
      console.log('🔄 Body should be updated now')
    }, 100)
  }

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  }

  return (
    <div>
      <ReactQuill
        value={body}
        onChange={setBody}
        modules={modules}
        style={{ 
          height: '300px', 
          marginBottom: '60px',
          background: 'white',
        }}
        placeholder="Write your reply..."
      />

      <Space style={{ marginTop: '12px' }}>
        <Button
          type="primary"
          icon={<MaterialIcon icon="send" style={{ fontSize: '18px' }} />}
          onClick={handleSend}
          loading={isSending}
          disabled={!body.trim()}
        >
          Send
        </Button>
        <Button
          icon={<MaterialIcon icon="add_shopping_cart" style={{ fontSize: '18px' }} />}
          onClick={() => setShowPriceBook(true)}
        >
          Add Items from Price Book
        </Button>
        <Button onClick={onCancel}>
          Cancel
        </Button>
      </Space>

      {/* Price Book Selector Modal */}
      <PriceBookSelector
        visible={showPriceBook}
        onClose={() => setShowPriceBook(false)}
        onAddItems={handleAddItems}
      />
    </div>
  )
}

export default EmailComposer

